import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const renderBlock = (block, i) => {
  if (block.type === "ul") {
    return (
      <ul key={i} className="space-y-2 pl-1">
        {block.items.map((it, j) => (
          <li key={j} className="flex gap-2.5">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "contact") {
    return (
      <div key={i} className="bg-card border border-border rounded-xl p-5 not-prose">
        <div className="font-display font-semibold text-foreground">{block.name}</div>
        <div className="mt-1">{block.address}</div>
        <a href={`mailto:${block.email}`} className="mt-1 inline-block text-primary hover:underline">
          {block.email}
        </a>
      </div>
    );
  }
  return <p key={i}>{block.text}</p>;
};

const LegalLayout = ({ title, eyebrow = "Legal", intro = [], sections = [] }) => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-background/80 border-b border-border/40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="legal-logo">
            <div className="w-8 h-8 rounded-lg bg-primary grid place-items-center text-primary-foreground font-bold">P</div>
            <span className="font-display text-xl font-bold tracking-tight">PAYD</span>
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard"><Button data-testid="legal-app">Back to app</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" data-testid="legal-login">Sign in</Button></Link>
                <Link to="/register"><Button data-testid="legal-signup">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">{eyebrow}</div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tighter">{title}</h1>
        {intro.length > 0 && (
          <div className="mt-6 space-y-4 text-[15px] text-muted-foreground leading-relaxed">
            {intro.map((t, i) => <p key={i}>{t}</p>)}
          </div>
        )}

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="flex items-baseline gap-3 text-lg font-display font-semibold tracking-tight">
                <span className="grid place-items-center w-7 h-7 shrink-0 rounded-full bg-primary/10 text-primary text-sm font-bold self-start">{i + 1}</span>
                <span>{s.title}</span>
              </h2>
              <div className="mt-3 pl-10 space-y-3 text-[15px] text-muted-foreground leading-relaxed">
                {s.blocks.map(renderBlock)}
              </div>
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} PAYD</span>
          <div className="flex items-center gap-5">
            <Link to="/help" className="hover:text-foreground transition-colors">Help</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalLayout;
